import React, { forwardRef } from 'react'
import { ChevronDown, ChevronUp, Search } from 'lucide-react'

import { cn } from '../utils/cn'

import { Input } from './Input'
import { Badge } from './Badge'
import { Skeleton } from './Skeleton'

export interface Column<T = any> {
  key: string
  header: string
  accessor?: keyof T | ((row: T) => React.ReactNode)
  sortable?: boolean
  width?: string
  className?: string
  render?: (value: any, row: T, index: number) => React.ReactNode
}

export interface TableProps<T = any> {
  data: T[]
  columns: Column<T>[]
  loading?: boolean
  searchable?: boolean
  searchPlaceholder?: string
  searchValue?: string
  onSearchChange?: (value: string) => void
  sortBy?: string
  sortDirection?: 'asc' | 'desc'
  onSort?: (key: string, direction: 'asc' | 'desc') => void
  emptyMessage?: string
  className?: string
  rowClassName?: string | ((row: T, index: number) => string)
  onRowClick?: (row: T, index: number) => void
}

const Table = forwardRef<HTMLTableElement, TableProps>(
  (
    {
      data,
      columns,
      loading = false,
      searchable = false,
      searchPlaceholder = 'Search...',
      searchValue,
      onSearchChange,
      sortBy,
      sortDirection,
      onSort,
      emptyMessage = 'No data available',
      className,
      rowClassName,
      onRowClick,
      ...props
    },
    ref,
  ) => {
    const handleSort = (key: string) => {
      if (!onSort) return

      const newDirection = sortBy === key && sortDirection === 'asc' ? 'desc' : 'asc'

      onSort(key, newDirection)
    }

    const renderCell = (column: Column, row: any, index: number) => {
      if (column.render) {
        return column.render(
          typeof column.accessor === 'function' ? column.accessor(row) : row[column.accessor as string],
          row,
          index,
        )
      }

      const value = typeof column.accessor === 'function' ? column.accessor(row) : row[column.accessor as string]

      return value
    }

    const getRowClassName = (row: any, index: number) => {
      if (typeof rowClassName === 'function') {
        return rowClassName(row, index)
      }

      return rowClassName
    }

    return (
      <div className="space-y-4">
        {searchable ? (
          <div className="max-w-sm">
            <Input
              leftIcon={<Search className="h-4 w-4" />}
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={e => onSearchChange?.(e.target.value)}
            />
          </div>
        ) : null}

        <div className="overflow-hidden rounded-lg border border-gray-200">
          <div className="overflow-x-auto">
            <table ref={ref} className={cn('min-w-full divide-y divide-gray-200', className)} {...props}>
              <thead className="bg-gray-50">
                <tr>
                  {columns.map(column => (
                    <th
                      key={column.key}
                      className={cn(
                        'px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider',
                        column.sortable && 'cursor-pointer hover:bg-gray-100',
                        column.className,
                      )}
                      style={{ width: column.width }}
                      onClick={() => column.sortable && handleSort(column.key)}
                    >
                      <div className="flex items-center space-x-1">
                        <span>{column.header}</span>
                        {column.sortable ? (
                          <div className="flex flex-col">
                            <ChevronUp
                              className={cn(
                                'h-3 w-3',
                                sortBy === column.key && sortDirection === 'asc' ? 'text-gray-900' : 'text-gray-400',
                              )}
                            />
                            <ChevronDown
                              className={cn(
                                'h-3 w-3 -mt-1',
                                sortBy === column.key && sortDirection === 'desc' ? 'text-gray-900' : 'text-gray-400',
                              )}
                            />
                          </div>
                        ) : null}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  // Loading skeleton rows
                  Array.from({ length: 5 }).map((_, index) => (
                    <tr key={index}>
                      {columns.map(column => (
                        <td key={column.key} className="px-6 py-4 whitespace-nowrap">
                          <Skeleton className="h-4 w-full" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : data.length === 0 ? (
                  <tr>
                    <td className="px-6 py-12 text-center text-gray-500" colSpan={columns.length}>
                      {emptyMessage}
                    </td>
                  </tr>
                ) : (
                  data.map((row, index) => (
                    <tr
                      key={index}
                      className={cn('hover:bg-gray-50', onRowClick && 'cursor-pointer', getRowClassName(row, index))}
                      onClick={() => onRowClick?.(row, index)}
                    >
                      {columns.map(column => (
                        <td
                          key={column.key}
                          className={cn('px-6 py-4 whitespace-nowrap text-sm text-gray-900', column.className)}
                        >
                          {renderCell(column, row, index)}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    )
  },
)

Table.displayName = 'Table'

export { Table }
